import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from "react";
import {
  Node,
  useReactFlow,
  getConnectedEdges,
  Edge,
  XYPosition,
} from "@xyflow/react";

interface CopyPasteContextType {
  bufferedNodes: Node[];
  bufferedEdges: Edge[];
  copy: (id: string) => void;
  cut: () => void;
  paste: (position?: {x: number; y: number}) => void;
}

const CopyPasteContext = createContext<CopyPasteContextType | undefined>(
  undefined,
);

export const useCopyPasteContext = () => {
  const context = useContext(CopyPasteContext);
  if (!context) {
    throw new Error(
      "useCopyPasteContext must be used within a CopyPasteProvider",
    );
  }
  return context;
};

export const CopyPasteProvider: React.FC<{children: ReactNode}> = ({
  children,
}) => {
  const mousePosRef = useRef<XYPosition>({x: 0, y: 0});

  const {getNodes, setNodes, getEdges, setEdges, screenToFlowPosition} =
    useReactFlow();

  const [bufferedNodes, setBufferedNodes] = useState<Node[]>([]);
  const [bufferedEdges, setBufferedEdges] = useState<Edge[]>([]);

  const copy = useCallback(
    (id: string) => {
      const selectedNodes = getNodes().filter(node => node.id === id);
      const selectedEdges = getConnectedEdges(selectedNodes, getEdges()).filter(
        edge => {
          const isExternalSource = selectedNodes.every(
            n => n.id !== edge.source,
          );
          const isExternalTarget = selectedNodes.every(
            n => n.id !== edge.target,
          );

          return !(isExternalSource || isExternalTarget);
        },
      );
      setBufferedNodes(selectedNodes);
      setBufferedEdges(selectedEdges);
    },
    [getNodes, getEdges],
  );

  const cut = useCallback(() => {
    const selectedNodes = getNodes().filter(node => node.selected);
    const selectedEdges = getConnectedEdges(selectedNodes, getEdges()).filter(
      edge => {
        const isExternalSource = selectedNodes.every(n => n.id !== edge.source);
        const isExternalTarget = selectedNodes.every(n => n.id !== edge.target);

        return !(isExternalSource || isExternalTarget);
      },
    );

    setBufferedNodes(selectedNodes);
    setBufferedEdges(selectedEdges);

    // A cut action needs to remove the copied nodes and edges from the graph.
    setNodes(nodes => nodes.filter(node => !node.selected));
    setEdges(edges => edges.filter(edge => !selectedEdges.includes(edge)));
  }, [getNodes, setNodes, getEdges, setEdges]);

  const paste = useCallback(
    (
      {x: pasteX, y: pasteY} = screenToFlowPosition({
        x: mousePosRef.current.x,
        y: mousePosRef.current.y,
      }),
    ) => {
      if (bufferedNodes.length === 0) return;

      const minX = Math.min(...bufferedNodes.map(s => s.position.x));
      const minY = Math.min(...bufferedNodes.map(s => s.position.y));

      const now = Date.now();

      const newNodes: Node[] = bufferedNodes.map(node => {
        const id = `${node.id}-${now}`;
        const x = pasteX + (node.position.x - minX);
        const y = pasteY + (node.position.y - minY);

        return {...node, id, position: {x, y}};
      });

      const newEdges: Edge[] = bufferedEdges.map(edge => {
        const id = `${edge.id}-${now}`;
        const source = `${edge.source}-${now}`;
        const target = `${edge.target}-${now}`;

        return {...edge, id, source, target};
      });

      setNodes(nodes => [
        ...nodes.map(node => ({...node, selected: false})),
        ...newNodes,
      ]);
      setEdges(edges => [
        ...edges.map(edge => ({...edge, selected: false})),
        ...newEdges,
      ]);
    },
    [bufferedNodes, bufferedEdges, screenToFlowPosition, setNodes, setEdges],
  );

  const value: CopyPasteContextType = {
    bufferedNodes,
    bufferedEdges,
    copy,
    cut,
    paste,
  };

  return (
    <CopyPasteContext.Provider value={value}>
      {children}
    </CopyPasteContext.Provider>
  );
};
